import React, { useRef, useEffect } from 'react';

const RhythmicPreview: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let width = 0;
    let height = 0;
    let centerX = 0;
    let centerY = 0;
    let containerRadius = 150;

    interface Ball {
      x: number;
      y: number;
      targetX: number;
      targetY: number;
      progress: number;
      speed: number;
      color: string;
      radius: number;
    }

    interface Point { x: number; y: number }
    let trail: Point[] = [];
    const trailLength = 40;

    const ball: Ball = {
      x: 0,
      y: 0,
      targetX: 0,
      targetY: 0,
      progress: 0,
      speed: 0.04,
      color: '#0f172a',
      radius: 10
    };

    let lastHitAngle = 0;
    let nextHitAngle = Math.PI;

    const setNextTarget = () => {
      lastHitAngle = nextHitAngle;
      nextHitAngle = lastHitAngle + Math.PI * (0.4 + Math.random() * 0.6);
      ball.targetX = Math.cos(nextHitAngle) * (containerRadius - ball.radius);
      ball.targetY = Math.sin(nextHitAngle) * (containerRadius - ball.radius);
      ball.progress = 0;
      ball.color = `hsla(${Math.random() * 360}, 100%, 50%, 1)`;
    };

    const resize = () => {
      if (containerRef.current && canvas) {
        const { clientWidth, clientHeight } = containerRef.current;
        width = clientWidth;
        height = clientHeight;
        canvas.width = width * window.devicePixelRatio;
        canvas.height = height * window.devicePixelRatio;
        ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
        centerX = width / 2;
        centerY = height / 2;
        containerRadius = (Math.min(width, height) / 2) * 0.8;
        setNextTarget();
      }
    };

    window.addEventListener('resize', resize);
    resize();

    const update = () => {
      ball.progress += ball.speed;
      if (ball.progress >= 1) {
        ball.progress = 1;
        setNextTarget();
      }

      // Smooth interpolation for "rhythmic" feel
      const startX = Math.cos(lastHitAngle) * (containerRadius - ball.radius);
      const startY = Math.sin(lastHitAngle) * (containerRadius - ball.radius);
      
      // Arc path
      ball.x = startX + (ball.targetX - startX) * ball.progress;
      ball.y = startY + (ball.targetY - startY) * ball.progress;
      
      // Simple bounce height effect
      const peak = Math.sin(ball.progress * Math.PI) * 40;
      const angle = Math.atan2(ball.targetY - startY, ball.targetX - startX) + Math.PI/2;
      ball.x += Math.cos(angle) * peak;
      ball.y += Math.sin(angle) * peak;

      // Update Trail
      trail.unshift({ x: ball.x, y: ball.y });
      if (trail.length > trailLength) {
        trail.pop();
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      
      ctx.save();
      ctx.translate(centerX, centerY);

      // Spectrum-like ring
      ctx.beginPath();
      ctx.arc(0, 0, containerRadius, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.fill();
      const grad = ctx.createLinearGradient(-containerRadius, -containerRadius, containerRadius, containerRadius);
      grad.addColorStop(0, '#00e5ff');
      grad.addColorStop(0.5, '#d500f9');
      grad.addColorStop(1, '#ffea00');
      ctx.strokeStyle = grad;
      ctx.lineWidth = 4;
      ctx.stroke();

      // Pulse on hit
      if (ball.progress < 0.1) {
        ctx.beginPath();
        ctx.arc(0, 0, containerRadius + (0.1 - ball.progress) * 100, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(0, 229, 255, ${0.1 - ball.progress})`;
        ctx.stroke();
      }

      // Draw Trail
      if (trail.length > 1) {
        ctx.beginPath();
        ctx.moveTo(trail[0].x, trail[0].y);
        for (let i = 1; i < trail.length; i++) {
          ctx.lineTo(trail[i].x, trail[i].y);
        }
        ctx.strokeStyle = ball.color;
        ctx.globalAlpha = 0.3;
        ctx.lineWidth = 4;
        ctx.lineJoin = 'round';
        ctx.stroke();
        ctx.globalAlpha = 1.0;
      }

      // Ball
      ctx.beginPath();
      ctx.arc(ball.x, ball.y, ball.radius, 0, Math.PI * 2);
      ctx.fillStyle = ball.color;
      ctx.fill();

      ctx.restore();
    };

    const loop = () => {
      update();
      draw();
      animationFrameId = requestAnimationFrame(loop);
    };

    loop();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <div ref={containerRef} className="group relative rounded-[3rem] overflow-hidden aspect-square bg-[#f8fafc] border border-zinc-200 hover:border-black transition-all duration-500 shadow-2xl">
      <canvas ref={canvasRef} className="block" />
      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-white to-transparent pointer-events-none">
        <h3 className="text-black font-bold text-lg uppercase tracking-tight">MUSIC</h3>
        <p className="text-zinc-600 text-[10px] font-black uppercase tracking-[0.2em] mt-1">Bouncing to the Beat</p>
      </div>
    </div>
  );
};

export default RhythmicPreview;
